/**
 * Dashboard configuration schema.
 *
 * Stored as structured data (never rendered HTML) so dashboards can be
 * re-opened in the builder, migrated forward, and rendered by different
 * components over time.
 *
 * VERSIONING: bump DASHBOARD_SCHEMA_VERSION whenever the shape changes and add
 * a step to `migrateDashboard` in services/dashboardService.ts. Old dashboards
 * saved by earlier builds must keep opening — this app ships to multiple
 * clients, so a breaking schema change is a data-loss bug.
 */

import type { LayerGeometryType, LayerRenderer } from '../../types';
import type { LayerLabeling } from '../../labeling/LabelTypes';

export const DASHBOARD_SCHEMA_VERSION = 2;

/* ────────────────────────────── Data sources ────────────────────────────── */

/**
 * Where a source's records come from.
 *
 * `local` has no service behind it at all: the features were imported from a
 * file on the admin's machine and travel INSIDE the dashboard, so every query
 * a widget makes is answered in the browser. See services/localSource.ts.
 */
export type DashboardServiceType = 'geoserver' | 'arcgis' | 'local';

/**
 * One queryable layer added to the dashboard.
 *
 * Each source carries its OWN service URL. This differs deliberately from
 * AppModule, which holds a single geoServerUrl/arcgisUrl for the whole map — a
 * dashboard is expected to pull indicators from several services at once.
 */
export interface DashboardDataSource {
  id: string;
  /** Display name; defaults to the service layer name, admin-editable. */
  name: string;
  serviceType: DashboardServiceType;
  /** Service root: GeoServer base (…/geoserver/ws) or ArcGIS Feature/MapServer. */
  serviceUrl: string;
  /** WMS/WFS typeName, or the ArcGIS layer's service name. */
  layerName: string;
  /** Present only for ArcGIS sources. */
  arcgisLayerId?: number;
  geometryType: LayerGeometryType;
  addedAt: string;

  /**
   * The features themselves, for a `local` source. Absent for every other kind.
   *
   * CARRIED IN THE DASHBOARD, NOT FETCHED
   * A file has no service to query, so the records have to live somewhere the
   * widgets can reach on every render — and the only place that survives a
   * reload, a different browser, and a viewer who never had the file is the
   * saved dashboard itself. This is the same trade the map builder already
   * makes with `Layer.localData`: the document gets big, and in exchange an
   * imported layer behaves exactly like a published one.
   */
  localData?: GeoJSON.FeatureCollection;

  /** Where a local source's features came from, shown in the source list. */
  originFile?: string;
}

/** A queryable attribute on a data source. */
export interface DashboardField {
  name: string;
  alias: string;
  /** Normalised so widgets can offer numeric-only statistics. */
  type: 'number' | 'string' | 'date' | 'other';
  /** Esri coded-value domain, used to show labels instead of raw codes. */
  domain?: { code: string | number; label: string }[];
}

/* ──────────────────────────────── Filtering ─────────────────────────────── */

export type FilterOperator =
  | '='
  | '<>'
  | '>'
  | '<'
  | '>='
  | '<='
  | 'contains'
  | 'startsWith'
  | 'isNull'
  | 'isNotNull';

export interface FilterClause {
  id: string;
  field: string;
  operator: FilterOperator;
  value: string;
  /** Joins this clause to the PREVIOUS one; ignored on the first clause. */
  conjunction: 'AND' | 'OR';
}

/* ──────────────────────────────── Selectors ──────────────────────────────── */

/**
 * A header filter control — Esri Operations Dashboard's "Selectors": Category,
 * Number and Date. Deliberately NOT a `DashboardWidget`: a selector has no grid
 * position, draws no data of its own, and its "action" is always the same
 * (narrow every widget on its own data source) rather than something an admin
 * wires per-target the way a chart's click action is. See `selectorRuntime.ts`
 * for how a selector's current value becomes a WHERE clause, and
 * `DashboardSelectorBar` for where it renders.
 */
export type SelectorType = 'category' | 'number' | 'date';

export interface DashboardSelector {
  id: string;
  type: SelectorType;
  /** The control's own caption in the header. */
  label: string;
  dataSourceId: string;
  field: string;
  /**
   * Category only. Off = a plain dropdown (`field = value`); on = a
   * multi-value list (`field IN (...)`).
   */
  multiple?: boolean;
  /**
   * Number and Date only. `single` is one dropdown/calendar; `range` is two
   * (From/To), narrowing with `>=`/`<=` instead of `=`. Meaningless — and
   * unused — for `category`, which has `multiple` instead.
   */
  mode?: 'single' | 'range';
}

/* ──────────────────────────────── Widgets ───────────────────────────────── */

export type WidgetType =
  | 'kpi'
  | 'gauge'
  | 'pie'
  | 'donut'
  | 'bar'
  | 'column'
  | 'line'
  | 'table'
  | 'list'
  | 'map'
  | 'text';

export type StatisticType = 'count' | 'sum' | 'avg' | 'min' | 'max';

/** Position on the 12-column layout grid. Units are grid cells, not pixels. */
export interface WidgetLayout {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Per-widget settings.
 *
 * Deliberately one open interface with optional members rather than a
 * discriminated union: new widget types can add keys without forcing a schema
 * migration, and the editor can read/write config generically. Each renderer
 * documents which keys it consumes.
 */
export interface WidgetConfig {
  /* KPI + Gauge */
  field?: string;
  statistic?: StatisticType;
  prefix?: string;
  suffix?: string;
  decimals?: number;
  /** KPI caption under the value. */
  caption?: string;

  /* Gauge */
  minValue?: number;
  maxValue?: number;
  targetValue?: number;
  /** Ascending thresholds; the highest one below the value wins. */
  thresholds?: { value: number; color: string }[];

  /* Charts */
  categoryField?: string;
  /** Statistic applied per category. `count` ignores `field`. */
  maxCategories?: number;
  sortBy?: 'value-desc' | 'value-asc' | 'category-asc' | 'category-desc';
  showLegend?: boolean;
  showValueLabels?: boolean;
  palette?: string[];
  /**
   * Which way the bars run.
   *
   * Absent keeps each widget type's historic shape — `bar` horizontal, `column`
   * vertical — so dashboards saved before this option look exactly as they did.
   * Setting it lets one type do both, which is how a user thinks about it:
   * "a bar chart, turned the other way".
   */
  orientation?: 'horizontal' | 'vertical';
  /** Axis titles. Empty means no title, which is the right default for a tile. */
  xAxisLabel?: string;
  yAxisLabel?: string;

  /* List */
  /**
   * `features` gives one row per record; `grouped` gives one row per DISTINCT
   * value of `titleField`, with a statistic beside it — Esri's "Features" vs
   * "Grouped values" data setting.
   *
   * Absent means `features`, so lists saved before grouping existed keep
   * rendering exactly as they did.
   */
  listMode?: 'features' | 'grouped';
  /** Each row's main line — the field to GROUP BY in `grouped` mode. */
  titleField?: string;
  /** Optional second line under the title. */
  subtitleField?: string;
  /** Optional value pinned to the right of the row. */
  listValueField?: string;
  /** Colours the leading dot; useful for a status column. */
  listColorField?: string;
  maxItems?: number;
  /**
   * Lets an end user pick more than one row instead of one at a time.
   *
   * Absent means single-select, the original behaviour: picking a row
   * broadcasts it alone and picking it again clears it. On, a row toggles in
   * and out of a running set, and the selection action broadcasts every
   * picked value together (see `ActionSignal`'s `values`) rather than
   * replacing the previous one.
   */
  multiSelect?: boolean;
  /**
   * Shows a small search box above the list. Typing narrows the rows to ones
   * whose `titleField` contains the text (server-side, so it reaches records
   * beyond `maxItems`, not just what is already on screen), and suggests
   * matching distinct values underneath as the user types.
   */
  listSearch?: boolean;

  /* Table */
  columns?: string[];
  pageSize?: number;
  orderByField?: string;
  orderDirection?: 'asc' | 'desc';

  /* Map */
  /** Data source ids drawn on the map widget. */
  mapDataSourceIds?: string[];
  basemap?: 'street' | 'terrain' | 'satellite' | 'dark';
  center?: [number, number];
  zoom?: number;
  /**
   * Symbology + labels per drawn data source, keyed by DashboardDataSource.id.
   *
   * Lives on the WIDGET, not the source: one DashboardDataSource can be drawn
   * by more than one map widget on the same dashboard, and each may
   * reasonably want its own look (one tile as a quick overview, another
   * colour-coded by status) — the same relationship the main Application
   * Builder already has between a service and the `Layer` instances built
   * from it, where `renderer`/`labeling` live on the `Layer`, not the
   * connection.
   */
  mapLayerStyles?: Record<string, { renderer?: LayerRenderer; labeling?: LayerLabeling }>;

  /* Text */
  heading?: string;
  body?: string;
  align?: 'left' | 'center' | 'right';

  /* Shared */
  color?: string;
  /** Per-widget card border colour — unset keeps the theme's own hairline
   *  (see WidgetFrame). Independent of `color`, which tints the widget's own
   *  content (a KPI number, a chart), not its box. */
  outlineColor?: string;
}

export interface DashboardWidget {
  id: string;
  type: WidgetType;
  title: string;
  /** Omitted for text widgets and (optionally) map widgets. */
  dataSourceId?: string;
  filters: FilterClause[];
  config: WidgetConfig;
  layout: WidgetLayout;
  /**
   * What this widget does to OTHER widgets when the user interacts with it.
   * Absent on widgets saved before actions existed, hence optional.
   */
  actions?: WidgetAction[];
}

/* ──────────────────────────────── Actions ───────────────────────────────── */

/**
 * What the user did on the SOURCE widget.
 *  - `selection` : picked a category on a chart, or a row in a table
 *  - `extent`    : panned or zoomed a map widget
 * The trigger a widget can raise follows from its type, so the editor offers
 * only the one that applies rather than asking the user to know.
 */
export type ActionTrigger = 'selection' | 'extent';

/**
 * What happens to a target when the source is interacted with.
 *  - `filter` : narrow the target's data (charts, KPIs, tables, lists)
 *  - `select` : highlight the matching features on a map
 *  - `zoom`   : frame the map on the matching features
 *  - `pan`    : recentre the map without changing its zoom
 *
 * A source can drive several kinds at once — one `WidgetAction` per kind — so
 * "highlight it and zoom to it" is two entries, not a new schema shape.
 */
export type ActionKind = 'filter' | 'select' | 'zoom' | 'pan';

/** Action kinds that make sense for a given target. */
export function kindsForTarget(targetType: WidgetType): ActionKind[] {
  // Only a map can highlight or move; everything else can only be filtered.
  if (targetType === 'map') return ['select', 'zoom', 'pan'];
  if (targetType === 'text') return [];
  return ['filter'];
}

export const ACTION_KIND_LABELS: Record<ActionKind, string> = {
  filter: 'Filter',
  select: 'Select',
  zoom: 'Zoom to',
  pan: 'Pan to',
};

/**
 * Ticked when a target is first added.
 *
 * Selecting is the least destructive useful default: it shows the user what
 * they picked without silently rewriting every other number on the board.
 */
export function defaultKindsForTarget(targetType: WidgetType): ActionKind[] {
  return targetType === 'map' ? ['select'] : ['filter'];
}

/**
 * How a filter action matches.
 *
 *  - `attribute` : the target keeps rows whose FIELD equals the selected value.
 *    Needs the two layers to share a column that means the same thing — a ward
 *    code, an asset id — and is exact when they do.
 *  - `spatial`   : the target keeps features that fall inside the selected
 *    SHAPE. Needs no shared column at all, which is the point: a ward polygon
 *    can filter a pipe layer that has never heard of wards.
 *
 * The same pair Esri offers, and for the same reason — the two questions
 * ("same code as this" and "inside this") are genuinely different, and which
 * one is meant cannot be guessed from the data.
 */
export type ActionMatch = 'attribute' | 'spatial';

export const ACTION_MATCH_LABELS: Record<ActionMatch, string> = {
  attribute: 'By attribute',
  spatial: 'By location',
};

/**
 * Which column on each side an attribute match compares.
 *
 * WHY BOTH SIDES ARE NAMED
 * Two layers routinely carry the same fact under different names — a ward is
 * `WARD_NO` on the boundary layer and `WARDNO` on the pipes, an asset id is
 * `FACILITYID` in one system and `ASSET_ID` in the next. The values match; only
 * the headings differ. Comparing by a single name would work for exactly the
 * datasets that were built together and fail for every other pair, so the
 * pairing is asked for rather than assumed.
 *
 * Both sides are optional. Absent means "the field the source already
 * broadcasts", which is what every action saved before this did.
 */
export interface ActionFieldPair {
  /** Field on the SOURCE layer whose value is broadcast. */
  source?: string;
  /** Field on the TARGET layer that value is compared against. */
  target?: string;
}

export interface WidgetAction {
  id: string;
  trigger: ActionTrigger;
  kind: ActionKind;
  /**
   * Only meaningful for `filter`. Absent means `attribute`, which is what
   * every action saved before this did.
   */
  match?: ActionMatch;
  /**
   * Field pairing per TARGET widget id, for attribute matching.
   *
   * Keyed by target because one action can drive several widgets and each sits
   * on its own layer with its own column names — a single pair on the action
   * would force every target to use the same heading.
   */
  fieldMap?: Record<string, ActionFieldPair>;
  /** Widgets that react. An empty list means the action does nothing. */
  targetWidgetIds: string[];
}

/**
 * A live constraint contributed by one source widget.
 *
 * Kept as data rather than a pre-built SQL string because the same selection
 * has to become an ArcGIS `where`, a GeoServer `CQL_FILTER`, and a readable
 * chip in the UI — three renderings of one fact.
 */
export type ActionSignal =
  | {
      kind: 'selection';
      sourceId: string;
      sourceTitle: string;
      field: string;
      value: string;
      /**
       * Every value currently picked, when the source allows more than one
       * (`WidgetConfig.multiSelect`). `value` stays the single/first one for
       * every consumer written before this existed — a chip label, an
       * un-mapped comparison — so this is additive: present and holding two
       * or more entries only when a multi-select source actually has more
       * than one row picked.
       */
      values?: string[];
      /**
       * The shape of what was selected, when any of the source's filter
       * actions matches spatially.
       *
       * Resolved by the SOURCE before it broadcasts, not by each target: it is
       * one request for the selection either way, and doing it per target
       * would repeat it once per widget listening.
       */
      geometry?: GeoJSON.Geometry;
      /**
       * Stamped per TARGET as the signal is handed out, because one selection
       * can reach one widget by attribute and another by location.
       *
       * On the selection variant only — an extent is a shape already, so
       * asking how it matches would be a question with one answer.
       */
      match?: ActionMatch;
      /**
       * Values of source fields OTHER than `field`, read off the selected rows.
       *
       * Needed when a target compares against a column the source widget does
       * not display: a chart grouped by ward NAME still has to hand a pipe
       * layer the ward CODE. Resolved once at the source from the rows it
       * already had to fetch, and keyed by source field name.
       *
       * One field can hold several values — a name that maps to two codes — so
       * each is a list, and the target matches any of them.
       */
      fieldValues?: Record<string, string[]>;
      /**
       * Stamped per TARGET alongside `match`: the columns this particular
       * target compares. Absent means both sides use `field`.
       */
      pair?: ActionFieldPair;
    }
  | {
      kind: 'extent';
      sourceId: string;
      sourceTitle: string;
      /** [minLon, minLat, maxLon, maxLat] in WGS84. */
      extent: [number, number, number, number];
    };

/** The trigger each widget type is able to raise, or null if it cannot. */
export function triggerForWidgetType(type: WidgetType): ActionTrigger | null {
  if (type === 'pie' || type === 'donut' || type === 'bar' || type === 'column' || type === 'line') {
    return 'selection';
  }
  if (type === 'table' || type === 'list') return 'selection';
  if (type === 'map') return 'extent';
  return null;
}

/* ─────────────────────────────── Dashboard ──────────────────────────────── */

export interface DashboardTheme {
  accentColor: string;
  /** Widget surfaces and page background follow this. */
  mode: 'light' | 'dark';
  /**
   * The dashboard's own logo, shown in its header (side set by `logoPosition`).
   *
   * Data URI or absolute URL; absent means the built-in compass mark. It lives
   * here rather than on AppModule.headerTheme because a dashboard has its own
   * header component and its own theme -- accentColor and mode are already
   * here, and appearance settings that live in two places are the ones that
   * disagree later. Both headers render the same HeaderLogo, so they still
   * look identical.
   */
  logoUrl?: string;
  /** Logo height in px; the width follows the image's aspect ratio. */
  logoSize?: number;
  /** Which side of the header bar the logo sits on. Absent means `'left'`,
   *  the header's historic layout. */
  logoPosition?: 'left' | 'right';
  /** Header bar's own background colour. Unset keeps the glass panel's
   *  default translucent tint — the header stays part of the glass system
   *  until an admin deliberately opts out with a solid colour. */
  headerBgColor?: string;
  /** Header title's text colour. Unset keeps the theme's own dark/light
   *  default. Set together with `headerBgColor` in practice — a custom
   *  background is the reason the default text colour stops reading. */
  headerTextColor?: string;
  /**
   * Default border colour for every widget's box, dashboard-wide — set once
   * here to outline every tile the same way, rather than opening each
   * widget's own config and setting `WidgetConfig.outlineColor` one at a
   * time. That per-widget field still wins where it is set (see
   * `WidgetRenderer`'s `widget.config.outlineColor ?? dividerColor`), for the
   * rare tile that needs to stand out from the rest. Unset keeps the glass
   * card's own subtle hairline, exactly as dashboards saved before this
   * setting existed already render.
   */
  dividerColor?: string;
}

/**
 * Milliseconds between automatic widget refreshes. 0 = manual only, which is
 * the default: polling every dashboard by default would hammer client services.
 */
export type RefreshInterval = 0 | 60_000 | 300_000 | 900_000 | 1_800_000;

export interface DashboardConfig {
  schemaVersion: number;
  description: string;
  dataSources: DashboardDataSource[];
  widgets: DashboardWidget[];
  /** Header filter controls — see `DashboardSelector`'s own doc comment. */
  selectors: DashboardSelector[];
  theme: DashboardTheme;
  refreshInterval: RefreshInterval;
  updatedAt: string;
}

export const REFRESH_OPTIONS: { value: RefreshInterval; label: string }[] = [
  { value: 0, label: 'Manual' },
  { value: 60_000, label: 'Every minute' },
  { value: 300_000, label: 'Every 5 minutes' },
  { value: 900_000, label: 'Every 15 minutes' },
  { value: 1_800_000, label: 'Every 30 minutes' },
];

export const WIDGET_LIBRARY: {
  type: WidgetType;
  label: string;
  description: string;
  /** Whether the widget must be bound to a data source before it can render. */
  needsDataSource: boolean;
  defaultLayout: { w: number; h: number };
}[] = [
  {
    type: 'kpi',
    label: 'KPI / Indicator',
    description: 'A single headline number, such as a count or a total.',
    needsDataSource: true,
    defaultLayout: { w: 3, h: 2 },
  },
  {
    type: 'gauge',
    label: 'Gauge',
    description: 'A value against a minimum, maximum, and target.',
    needsDataSource: true,
    defaultLayout: { w: 3, h: 3 },
  },
  {
    type: 'pie',
    label: 'Pie chart',
    description: 'Share of a total, split by a category field.',
    needsDataSource: true,
    defaultLayout: { w: 4, h: 4 },
  },
  {
    type: 'donut',
    label: 'Donut chart',
    description: 'Pie chart with the total shown in the centre.',
    needsDataSource: true,
    defaultLayout: { w: 4, h: 4 },
  },
  {
    type: 'bar',
    label: 'Bar chart',
    description: 'Horizontal bars — good for long category names.',
    needsDataSource: true,
    defaultLayout: { w: 4, h: 4 },
  },
  {
    type: 'column',
    label: 'Column chart',
    description: 'Vertical bars, compared across categories.',
    needsDataSource: true,
    defaultLayout: { w: 4, h: 4 },
  },
  {
    type: 'line',
    label: 'Line chart',
    description: 'A trend across ordered categories.',
    needsDataSource: true,
    defaultLayout: { w: 6, h: 4 },
  },
  {
    type: 'table',
    label: 'Table',
    description: 'Records from a layer, with chosen columns.',
    needsDataSource: true,
    defaultLayout: { w: 6, h: 4 },
  },
  {
    type: 'list',
    label: 'List',
    description: 'Records as a scrollable list — a title line per feature.',
    needsDataSource: true,
    defaultLayout: { w: 3, h: 4 },
  },
  {
    type: 'map',
    label: 'Map',
    description: 'A map drawing the dashboard’s own layers.',
    needsDataSource: false,
    defaultLayout: { w: 6, h: 5 },
  },
  {
    type: 'text',
    label: 'Text',
    description: 'A heading and free text.',
    needsDataSource: false,
    defaultLayout: { w: 4, h: 2 },
  },
];

/** Widget types whose value comes from a category breakdown. */
export const CHART_TYPES: WidgetType[] = ['pie', 'donut', 'bar', 'column', 'line'];

export const STATISTIC_LABELS: Record<StatisticType, string> = {
  count: 'Count',
  sum: 'Sum',
  avg: 'Average',
  min: 'Minimum',
  max: 'Maximum',
};

export const OPERATOR_LABELS: Record<FilterOperator, string> = {
  '=': 'is equal to',
  '<>': 'is not equal to',
  '>': 'is greater than',
  '<': 'is less than',
  '>=': 'is at least',
  '<=': 'is at most',
  contains: 'contains',
  startsWith: 'starts with',
  isNull: 'is blank',
  isNotNull: 'is not blank',
};

/** Operators that take no value input. */
export const VALUELESS_OPERATORS: FilterOperator[] = ['isNull', 'isNotNull'];
